import { useEffect, useState } from "react";
import { cn } from "@edusogno/lib/utils";
import { Document, Page, pdfjs } from "react-pdf";
import ContentLoader from "@edusogno/composites/content-loader";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

interface Props {
  url: string;
  pdfDetails: { maxPages: number; currentPage: number };
  setPdfDetails: React.Dispatch<
    React.SetStateAction<{ maxPages: number; currentPage: number }>
  >;
}

export default function PDFReader({ url, pdfDetails, setPdfDetails }: Props) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handler = () => {
      const pdfContainer = document.getElementById("pdf-reader-container");
      if (pdfContainer !== null) {
        const dimensions = pdfContainer.getBoundingClientRect();
        pdfContainer.style.setProperty("--pdf-h", `${dimensions.height}px`);
        pdfContainer.style.setProperty("--pdf-w", `${dimensions.width}px`);
      }
    };

    handler();

    window.addEventListener("resize", handler);

    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  const pages = Array.from(Array(pdfDetails.maxPages).keys(), (x) => x + 1);

  const [pageScale] = useState(1);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setLoading(false);
    setPdfDetails((prev) => ({ ...prev, maxPages: numPages }));
  }

  return (
    <div
      id="pdf-reader-container"
      className="space-y-4 w-full h-full flex flex-col"
    >
      <div
        className={cn(
          "flex flex-col gap-2 p-4 items-center bg-white h-[80%] rounded-[10px] w-full mx-auto flex-1 bg-white/40",
          {
            "justify-content items-center": loading,
            "w-full h-full": loading,
          },
        )}
      >
        <div
          className={cn(
            `overflow-y-auto custom-scroll h-full w-full justify-center items-start flex`,
            { hidden: loading },
          )}
        >
          <Document
            file={url}
            className={cn("overflow-x-hidden overflow-y-auto", {
              hidden: loading,
            })}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            <div className={"relative overflow-hidden"}>
              {pages.map((page, index) => (
                <Page
                  width={1920}
                  height={1080}
                  key={index}
                  scale={pageScale}
                  pageNumber={page}
                  className={cn(
                    page === pdfDetails.currentPage
                      ? "z-20 center !relative"
                      : "top-0 left-0 !absolute z-10 right",
                    "w-[calc(0.90 * var(--pdf-w))] h-full",
                  )}
                />
              ))}
            </div>
          </Document>
        </div>

        {loading && (
          <div className="flex h-full w-full items-center justify-center">
            <ContentLoader className={"m-auto self-center"} />
          </div>
        )}
      </div>
    </div>
  );
}
